// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import { useWeapons } from '../contexts/WeaponsContext';
import { useCredits } from '../contexts/CreditsContext';
import './WeaponShop.css';
import { usePurchaseConfirm, PurchaseConfirmBanner } from '../hooks/ui/usePurchaseConfirm';

const WeaponShop = ({ onClose, locationName }) => {
  const { credits, canAfford, removeCredits } = useCredits();
  const weaponsContext = useWeapons();
  const { pending, requestPurchase, confirm, cancel } = usePurchaseConfirm();
  
  const { 
    weapons = [], // Default to empty array if undefined
    unlockedSlots = 0, // Default to 0 if undefined
    getAvailableWeapons, 
    unlockSlot, 
    getNextSlotCost, 
    canPurchaseWeapon, 
    purchaseWeapon,
    sellWeapon,
    reloadAll
  } = weaponsContext || {};
  
  const [feedback, setFeedback] = useState(null);

  const availableWeapons = getAvailableWeapons ? getAvailableWeapons(locationName) || [] : [];
  
  // Safety check - if context isn't ready, don't render shop content
  if (!weaponsContext || !weapons) {
    return createPortal(
      <div className="weapon-shop-overlay" onClick={onClose}>
        <div className="weapon-shop-container" onClick={(e) => e.stopPropagation()}>
          <div className="weapon-shop-header">
            <h2>⚔️ WEAPONS ARSENAL</h2>
            <button className="refuel-modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="shop-section">
            <p>Loading weapons data...</p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const handleUnlockSlot = () => {
    const cost = getNextSlotCost();
    
    if (!cost) {
      showFeedback('All weapon slots already unlocked!', '#ffaa00');
      return;
    }

    if (!canAfford(cost)) {
      showFeedback(`Insufficient credits! Need ${cost} credits.`, '#ff4444');
      return;
    }

    removeCredits(cost, 'weapon slot unlock');
    const result = unlockSlot();
    
    if (result.success) {
      showFeedback(`Weapon slot ${result.newSlotCount} unlocked!`, '#00ff88');
    }
  };

  const handlePurchaseWeapon = (weaponId) => {
    const check = canPurchaseWeapon(weaponId);
    
    if (!check.canPurchase) {
      if (check.reason === 'No empty slots') {
        showFeedback('No available weapon slots! Unlock more slots first.', '#ff4444');
      } else if (check.reason === 'Weapon not found') {
        showFeedback('Weapon not available!', '#ff4444');
      }
      return;
    }

    const weaponData = check.weapon;
    if (!canAfford(weaponData.cost)) {
      showFeedback(`Insufficient credits! Need ${weaponData.cost} credits.`, '#ff4444');
      return;
    }

    // Find first empty slot
    const emptySlotIndex = weapons.slice(0, unlockedSlots).findIndex(w => !w.installed);
    if (emptySlotIndex === -1) {
      showFeedback('No available weapon slots!', '#ff4444');
      return;
    }

    const executePurchase = () => {
      removeCredits(weaponData.cost, `weapon: ${weaponId}`);
      const result = purchaseWeapon(weaponId, emptySlotIndex);
      if (result.success) {
        showFeedback(`${result.weapon.name} purchased and equipped!`, '#00ff88');
      }
    };
    requestPurchase(weaponData.name, weaponData.cost, executePurchase);
  };

  const handleReloadWeapons = () => {
    const installedWeapons = (weapons || []).filter(w => w && w.installed);
    const totalAmmoNeeded = installedWeapons.reduce((sum, w) => sum + ((w.maxAmmo || 0) - (w.ammo || 0)), 0);
    const reloadCost = Math.ceil(totalAmmoNeeded * 0.2); // 0.2 credits per ammo point
    
    if (totalAmmoNeeded === 0) {
      showFeedback('All weapons are fully loaded!', '#ffaa00');
      return;
    }
    
    if (!canAfford(reloadCost)) {
      showFeedback(`Insufficient credits! Need ${reloadCost} credits.`, '#ff4444');
      return;
    }
    
    removeCredits(reloadCost, 'weapon reload');
    reloadAll();
    showFeedback(`⚡ All weapons reloaded! Cost: ${reloadCost} credits`, '#00ff88');
  };

  const handleSellWeapon = (slotIndex) => {
    const result = sellWeapon(slotIndex);
    
    if (!result.success) {
      showFeedback(result.error, '#ff4444');
      return;
    }
    
    removeCredits(-result.sellPrice, `sold weapon: ${result.weaponName}`); // Negative to add credits
    showFeedback(`${result.weaponName} sold for ${result.sellPrice} credits!`, '#00ff88');
  };

  const showFeedback = (message, color) => {
    setFeedback({ message, color });
    setTimeout(() => setFeedback(null), 3000);
  };

  const nextSlotCost = getNextSlotCost();
  const canUnlockSlot = nextSlotCost !== null;

  return createPortal(
    <div className="weapon-shop-overlay" onClick={onClose}>
      <div className="weapon-shop-container" onClick={(e) => e.stopPropagation()}>
        <div className="weapon-shop-header">
          <h2>⚔️ WEAPONS ARSENAL</h2>
          <button className="refuel-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Credits Display */}
        <div className="shop-credits">
          <span className="credits-label">AVAILABLE CREDITS:</span>
          <span className="credits-value">{credits.toLocaleString()}</span>
        </div>

        {/* Purchase Confirmation */}
        <PurchaseConfirmBanner pending={pending} onConfirm={confirm} onCancel={cancel} />
        {/* Feedback Message */}
        {feedback && (
          <div className="shop-feedback" style={{ color: feedback.color }}>
            {feedback.message}
          </div>
        )}

        {/* Current Loadout */}
        <div className="shop-section">
          <h3>CURRENT LOADOUT ({(weapons || []).filter(w => w.installed).length}/{unlockedSlots} SLOTS)</h3>
          <div className="equipped-weapons">
            {(weapons || []).filter(w => w.installed).map((weapon, index) => {
              const slotIndex = weapons.findIndex(w => w.id === weapon.id);
              const installedWeaponsCount = (weapons || []).filter(w => w.installed).length;
              const canSell = installedWeaponsCount > 1; // Can only sell if more than 1 weapon equipped
              const sellPrice = Math.floor(weapon.cost * 0.5);
              
              return (
                <div key={weapon.id} className="equipped-weapon">
                  <div className="weapon-info">
                    <div className="weapon-name">{weapon.name}</div>
                    <div className="weapon-damage">{weapon.damage} DMG</div>
                    <div className="weapon-charge">{Math.round(weapon.ammo)}%</div>
                  </div>
                  {canSell ? (
                    <Button
                      variant="warning"
                      size="small"
                      onClick={() => handleSellWeapon(slotIndex)}
                    >
                      SELL ({sellPrice} CR)
                    </Button>
                  ) : (
                    <div className="cannot-sell-reason" title="Cannot sell last weapon - you must have at least one weapon equipped">
                      LAST WEAPON
                    </div>
                  )}
                </div>
              );
            })}
            {[...Array(Math.max(0, unlockedSlots - (weapons || []).filter(w => w.installed).length))].map((_, i) => (
              <div key={`empty-${i}`} className="empty-slot">
                EMPTY SLOT
              </div>
            ))}
          </div>
        </div>

        {/* Reload Weapons */}
        {(weapons || []).filter(w => w.installed).length > 0 && (
          <div className="shop-section">
            <h3>RELOAD WEAPONS</h3>
            <Button
              variant="success"
              size="large"
              onClick={handleReloadWeapons}
              disabled={
                (weapons || []).filter(w => w.installed).every(w => w.ammo >= w.maxAmmo) ||
                !canAfford(Math.ceil((weapons || []).filter(w => w.installed).reduce((sum, w) => sum + ((w.maxAmmo || 0) - (w.ammo || 0)), 0) * 0.2))
              }
            >
              RELOAD ALL WEAPONS - {Math.ceil((weapons || []).filter(w => w.installed).reduce((sum, w) => sum + ((w.maxAmmo || 0) - (w.ammo || 0)), 0) * 0.2)} CREDITS
            </Button>
          </div>
        )}

        {/* Unlock Slot */}
        {canUnlockSlot && (
          <div className="shop-section">
            <h3>UNLOCK WEAPON SLOT</h3>
            <Button
              variant="primary"
              size="large"
              onClick={handleUnlockSlot}
              disabled={!canAfford(nextSlotCost)}
            >
              UNLOCK SLOT {unlockedSlots + 1} - {nextSlotCost} CREDITS
            </Button>
          </div>
        )}

        {/* Available Weapons */}
        <div className="shop-section">
          <h3>AVAILABLE WEAPONS</h3>
          <div className="weapons-catalog">
            {availableWeapons.length === 0 ? (
              <div className="no-weapons">All weapons purchased!</div>
            ) : (
              availableWeapons.map((weapon) => {
                const check = canPurchaseWeapon(weapon.id);
                const canAffordWeapon = canAfford(weapon.cost);
                
                return (
                  <div key={weapon.id} className="catalog-weapon">
                    <div className="weapon-info">
                      <div className="weapon-header">
                        <span className="weapon-name">{weapon.name}</span>
                        <span className={`weapon-tier tier-${weapon.tier}`}>
                          TIER {weapon.tier}
                        </span>
                      </div>
                      <div className="weapon-stats">
                        <span className="stat-damage">{weapon.damage} DMG</span>
                        {weapon.specialEffect && (
                          <span className="stat-special">
                            {weapon.specialEffect === 'shieldBonus' && '⚡ Shield Bonus'}
                            {weapon.specialEffect === 'fastFire' && '⚡ Fast Fire'}
                            {weapon.specialEffect === 'armorPiercing' && '⚡ Armor Piercing'}
                          </span>
                        )}
                      </div>
                      <div className="weapon-description">{weapon.description}</div>
                    </div>
                    <Button
                      variant={!check.canPurchase || !canAffordWeapon ? "secondary" : "success"}
                      size="medium"
                      onClick={() => handlePurchaseWeapon(weapon.id)}
                      disabled={!check.canPurchase || !canAffordWeapon}
                    >
                      {!check.canPurchase && check.reason === 'No empty slots'
                        ? 'NO SLOTS'
                        : !canAffordWeapon
                        ? `${weapon.cost} CR (INSUFFICIENT)`
                        : `PURCHASE - ${weapon.cost} CR`}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WeaponShop;
